/*
 * Buying Journey analytics bridge.
 *
 * Turns the journey's internal events into GTM dataLayer pushes so every
 * customer decision is measurable in GA4: which areas people measure, which
 * colours and formats win, what gets skipped, where the funnel leaks, and
 * what reaches the cart.
 *
 * Transport: window.dataLayer (the store runs GTM with Analyzify). Events are
 * also mirrored to Shopify.analytics.publish when available, so a Shopify
 * Custom Pixel can subscribe to the same stream if that route is ever wanted.
 *
 * Design rules:
 * - Decisions are captured at their FINAL state, when the customer moves
 *   forward off a step, not on every keystroke. Going back and changing an
 *   answer re-fires with the new state; unchanged revisits are deduped.
 * - Params are low-cardinality and flat, so they can be registered as GA4
 *   custom dimensions and used in reports.
 * - Everything is wrapped so an analytics failure can never break the journey.
 * - Debug: localStorage.setItem('bk_debug', '1') logs every push.
 */
(function () {
  'use strict';

  var PREFIX = 'bk_';

  var sentComplete = {}; // step id -> JSON of the last payload sent

  function debug() {
    try {
      return window.localStorage.getItem('bk_debug') === '1';
    } catch (err) {
      return false;
    }
  }

  /*
   * GA4, sent straight from the theme.
   *
   * This used to go out only through a Shopify Custom Pixel. That pixel runs
   * in Shopify's sandbox, and when the sandbox rolled out it started throwing
   * before it could subscribe, so every event vanished for six days while GA4
   * still looked healthy (the app pixels kept reporting). Sending from here
   * removes the sandbox, the pixel runtime and its consent gate from the path,
   * and it is debuggable in the console like the rest of the journey.
   *
   * Nothing can double count: send_page_view is off and the only events sent
   * are the bk_ names, which nothing else in the stack fires. The client id is
   * lifted from the storefront's own _ga cookie so these attach to the same
   * GA4 user as normal tracking rather than inventing a second one.
   *
   * If the Custom Pixel is ever reconnected, disconnect one of the two or the
   * bk_ events will be counted twice.
   */
  var gaReady = false;

  function measurementId() {
    return window.CATNETS_GA4_ID || null;
  }

  function gtag() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
  }

  function clientIdFromCookie() {
    try {
      // _ga looks like GA1.1.1194686903.1722816000; the client id is the last
      // two segments.
      var match = document.cookie.match(/_ga=GA\d+\.\d+\.([0-9]+\.[0-9]+)/);
      return match ? match[1] : null;
    } catch (err) {
      return null;
    }
  }

  function startGa() {
    var id = measurementId();
    if (gaReady || !id || !document.head) return;
    gaReady = true;

    try {
      var script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(id);
      document.head.appendChild(script);

      gtag('js', new Date());

      var config = { send_page_view: false };
      var cid = clientIdFromCookie();
      if (cid) config.client_id = cid;
      if (debug()) config.debug_mode = true;

      gtag('config', id, config);
    } catch (err) {
      /* Never let tracking break the journey. */
    }
  }

  function send(name, params) {
    try {
      var payload = params || {};
      var event = PREFIX + name;

      window.dataLayer = window.dataLayer || [];
      var row = { event: event };
      for (var k in payload) row[k] = payload[k];
      window.dataLayer.push(row);

      // Straight to GA4, no pixel in the way.
      if (measurementId()) {
        startGa();
        gtag('event', event, payload);
      }

      if (debug() && window.console) console.log('[bk analytics]', event, payload);
    } catch (err) {
      /* Never let tracking break the journey. */
    }
  }

  function dollars(cents) {
    return Math.round((cents || 0)) / 100;
  }

  function cap(value) {
    var s = String(value == null ? '' : value);
    return s.length > 95 ? s.slice(0, 95) : s;
  }

  /* ---------------------------------------------------- per-step payloads */

  function measureParams(d) {
    // width_m and height_m are reported separately on purpose. cross_m is
    // whichever one the net has to span, which is the width on an alley but
    // the height on a fenceline, so reporting on it alone would silently mix
    // two different measurements in the same column. 0 means "not asked for
    // this space type" rather than "measured as zero".
    return {
      area: d.area,
      length_m: d.length || 0,
      width_m: d.width || 0,
      height_m: d.height || 0,
      cross_m: d.cover === 'height' ? d.height : d.width,
      ends: d.ends || 'none',
      area_m2: Math.round(d.totalArea || 0)
    };
  }

  function nettingParams(d) {
    // Wide ends split into their own net, so the step can complete with more
    // than one. Sizes and formats join with | ; single-net journeys report
    // exactly as before.
    var nets = d.nets && d.nets.length ? d.nets : [d];
    var sizes = [];
    var formats = [];
    var qty = 0;
    for (var i = 0; i < nets.length; i++) {
      sizes.push(nets[i].variantTitle);
      formats.push(nets[i].format);
      qty += nets[i].quantity || 0;
    }
    return {
      net_colour: d.colour,
      net_format: formats.join('|'),
      net_size: cap(sizes.join('|')),
      net_qty: qty,
      net_count: nets.length,
      net_value: dollars(d.totalPrice),
      zip_choice: d.zip ? (d.zip.tier || 'zip') : 'none',
      zip_qty: d.zip ? (d.zip.quantity || 1) : 0
    };
  }

  function hardwareParams(d) {
    var a = d.answers || {};
    var materials = [a.m1, a.m2, a.m3, a.m4].filter(Boolean).join('|');
    return {
      hw_style: a.style || 'professional',
      hw_materials: cap(materials),
      hw_rule: cap(d.rule),
      hw_items: (d.items || []).length,
      hw_value: dollars(d.totalPrice)
    };
  }

  function skywalksParams(d) {
    var items = d.items || [];
    var units = 0;
    var handles = [];
    for (var i = 0; i < items.length; i++) {
      units += items[i].quantity || 1;
      handles.push(items[i].handle);
    }
    return {
      sky_items: items.length,
      sky_units: units,
      sky_value: dollars(d.totalPrice),
      sky_products: cap(handles.join('|'))
    };
  }

  var BUILDERS = {
    measure: measureParams,
    netting: nettingParams,
    hardware: hardwareParams,
    skywalks: skywalksParams
  };

  /* ------------------------------------------------------------- wiring */

  function stepIndex(journey, id) {
    return journey.stepIndex.hasOwnProperty(id) ? journey.stepIndex[id] + 1 : 0;
  }

  function sendComplete(journey, id) {
    var state = journey.state.steps[id];
    if (!state || !state.complete || !state.data) return;

    var builder = BUILDERS[id];
    var params = builder ? builder(state.data) : {};
    params.step_name = id;
    params.step_index = stepIndex(journey, id);

    // Same decision twice (back, then forward without changing) sends nothing.
    var key = JSON.stringify(params);
    if (sentComplete[id] === key) return;
    sentComplete[id] = key;

    send('step_complete', params);
  }

  function attach(journey) {
    var settled = 0;
    for (var i = 0; i < journey.steps.length; i++) {
      if (journey.isSettled(journey.steps[i].id)) settled++;
    }

    send('journey_start', {
      resume: settled > 0 ? 1 : 0,
      settled_count: settled
    });

    send('step_view', {
      step_name: journey.state.current,
      step_index: stepIndex(journey, journey.state.current)
    });
  }

  function boot() {
    function onReady(journey) {
      try {
        attach(journey);
      } catch (err) { /* tracking must not break the journey */ }
    }

    if (window.CatnetsJourney) {
      onReady(window.CatnetsJourney);
    } else {
      document.addEventListener('journey:ready', function (e) {
        onReady(e.detail.journey);
      });
    }

    document.addEventListener('journey:step:enter', function (e) {
      try {
        var journey = e.detail.journey;
        var to = e.detail.step;
        var from = e.detail.from;

        send('step_view', {
          step_name: to,
          step_index: stepIndex(journey, to)
        });

        // Moving FORWARD off a step is the moment its decisions are final.
        if (from && journey.stepIndex[to] > journey.stepIndex[from]) {
          sendComplete(journey, from);
        }
      } catch (err) { /* never break the journey */ }
    });

    document.addEventListener('journey:step:skip', function (e) {
      try {
        if (e.detail.prompted) {
          send('exit_upsell_shown', { step_name: e.detail.step });
        } else {
          send('step_skip', { step_name: e.detail.step });
        }
      } catch (err) { /* never break the journey */ }
    });

    // Choosing to stay when the exit upsell asks. Capture phase, because the
    // modal's own handler closes it during bubble and the open state would be
    // gone by the time a bubble listener ran.
    document.addEventListener('click', function (e) {
      try {
        if (!e.target || !e.target.closest) return;
        var modal = document.querySelector('[data-journey-modal].is-open');
        if (modal && e.target.closest('[data-journey-modal-dismiss]')) {
          send('exit_upsell_stay', {
            step_name: modal.getAttribute('data-journey-modal-step') || ''
          });
        }
      } catch (err) { /* never break the journey */ }
    }, true);

    // The review step announces a successful cart add just before redirecting.
    document.addEventListener('journey:cart:added', function (e) {
      try {
        send('cart_added', {
          value: dollars(e.detail.value),
          items_count: e.detail.itemCount
        });
      } catch (err) { /* never break the journey */ }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
